// Raw sort keys + comparator shared by Zebra Search and Folders.
// Dependency-free on purpose: the node suite imports this file directly
// (fieldUtils pulls in a @platform alias that only Vite resolves).

// Sort key for a field — the RAW value, not the display string getFieldValue
// returns. Sorting on display strings is what put "25.4" and "16" below "3"
// in Zebra Search: numbers became text ("8" > "7" > … > "3" > "25.4" > "2"
// > "16") and dd/mm/yyyy dates compared day-first. Here numbers stay numbers,
// dates stay ISO (lexicographic = chronological), priority sorts by its id
// (1 = Highest), and empties come back as '' so compareSortValues can push
// them to the bottom. Mirrors Folders' getSortableValue (Structure.jsx) so
// the two tables order the same field the same way.
export const getSortableValue = (issue, fieldId) => {
  if (!issue) return '';
  switch (fieldId) {
    case 'key':
      return issue.key || '';
    case 'summary':
      return issue.fields?.summary || '';
    case 'status':
      return issue.fields?.status?.name || '';
    case 'assignee':
      return issue.fields?.assignee?.displayName || '';
    case 'reporter':
      return issue.fields?.reporter?.displayName || '';
    case 'priority':
      return parseInt(issue.fields?.priority?.id) || 999;
    case 'issuetype':
      return issue.fields?.issuetype?.name || '';
    case 'created':
      return issue.fields?.created || '';
    case 'updated':
      return issue.fields?.updated || '';
    case 'duedate':
      return issue.fields?.duedate || '';
    case 'statuscategorychangedate':
      return issue.fields?.statuscategorychangedate || '';
    case 'startDate':
      return issue.fields?.startDate || issue.fields?.customfield_10015 || '';
    case 'labels':
      return issue.fields?.labels?.join(', ') || '';
    case 'components':
      return issue.fields?.components?.map(c => c.name).join(', ') || '';
    case 'fixVersions':
      return issue.fields?.fixVersions?.map(v => v.name).join(', ') || '';
    case 'resolution':
      return issue.fields?.resolution?.name || '';
    case 'project':
      return issue.fields?.project?.name || '';
    case 'projectCategory':
      return issue.fields?.project?.projectCategory?.name || '';
    case 'timeoriginalestimate':
    case 'timeestimate':
    case 'timespent': {
      const secs = issue.fields?.[fieldId];
      return typeof secs === 'number' ? secs : '';
    }
    case 'parent':
      return issue.fields?.parent?.key || '';
    default:
      if (fieldId?.startsWith('customfield_')) {
        const value = issue.fields?.[fieldId];
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) {
          return value
            .filter(v => v != null)
            .map(v => (typeof v === 'object' ? (v.value || v.name || v.displayName || '') : String(v)))
            .join(', ');
        }
        if (typeof value === 'object') {
          return value.value || value.name || value.displayName || '';
        }
        return value;
      }
      return '';
  }
};

const FULLY_NUMERIC_RE = /^-?\d+(\.\d+)?$/;
const isFullyNumeric = (s) => typeof s === 'string' && FULLY_NUMERIC_RE.test(s.trim());

// The RAW number behind a field, or NaN. Aggregates (Σ sum/avg/min/max) and
// the Excel export used to parseFloat the DISPLAY string; once number fields
// display grouped ("50,119.45") that parse stops at the comma and a sum of
// the column becomes a sum of thousands. Number fields hydrate as JS numbers
// (transformers.js), text-stored numerics ("25.4") still count.
export const getNumericFieldValue = (issue, fieldId) => {
  const v = getSortableValue(issue, fieldId);
  if (typeof v === 'number') return v;
  return isFullyNumeric(v) ? parseFloat(v) : NaN;
};

// Ascending comparator over getSortableValue results (or formula results).
// Empties sort LAST regardless of direction — the caller negates the result
// for descending, so it must handle empties itself; see sortIssues below.
// Two numbers (or whole-numeric strings, e.g. "25.4" from a number field
// stored as text) compare numerically; everything else compares with
// { numeric: true } so "HR-2" precedes "HR-10" and ISO dates stay
// chronological.
export const compareSortValues = (a, b) => {
  if (a && typeof a === 'object' && 'text' in a) a = a.text;
  if (b && typeof b === 'object' && 'text' in b) b = b.text;
  const numA = typeof a === 'number' ? a : (isFullyNumeric(a) ? parseFloat(a) : NaN);
  const numB = typeof b === 'number' ? b : (isFullyNumeric(b) ? parseFloat(b) : NaN);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const isEmptySortValue = (v) => v === null || v === undefined || v === '' ||
  (typeof v === 'number' && Number.isNaN(v));

// Stable sort of `issues` by `keyOf(issue)` in `dir` ('asc' | 'desc').
// Empties always land at the bottom, matching Folders and Jira.
export const sortIssues = (issues, keyOf, dir = 'asc') => {
  const sign = dir === 'desc' ? -1 : 1;
  return [...issues].sort((a, b) => {
    const va = keyOf(a);
    const vb = keyOf(b);
    const ea = isEmptySortValue(va);
    const eb = isEmptySortValue(vb);
    if (ea && eb) return 0;
    if (ea) return 1;
    if (eb) return -1;
    return sign * compareSortValues(va, vb);
  });
};

